export type BitrateSample = { timestamp: number; bitrate: number };
export type BitratePoint = { timestamp: number; bitrate: number | null };
export type BitrateChartData = { points: BitratePoint[]; domain: [number, number] };
export type BitrateClockAnchor = { serverTime?: number; receivedAt: number };

export const EMPTY_BITRATE_CHART: BitrateChartData = { points: [], domain: [0, 1] };
export const GRID_BITRATE_WINDOW_MS = 60_000;
export const PLAYER_BITRATE_WINDOW_MS = 100_000;

export function normalizeBitrateSamples(input: unknown): BitrateSample[] {
  if (!Array.isArray(input)) return [];
  const byTimestamp = new Map<number, BitrateSample>();
  for (const sample of input) {
    if (!sample || !Number.isSafeInteger(sample.timestamp) || sample.timestamp <= 0 ||
        sample.timestamp > 8.64e15 || typeof sample.bitrate !== "number" ||
        !Number.isFinite(sample.bitrate) || sample.bitrate < 0) continue;
    byTimestamp.set(sample.timestamp, { timestamp: sample.timestamp, bitrate: sample.bitrate });
  }
  return [...byTimestamp.values()].sort((a, b) => a.timestamp - b.timestamp);
}

// Use the server's clock and monotonic elapsed time, never the browser's wall clock.
export function bitrateServerNow(anchor: BitrateClockAnchor | undefined, monotonicNow: number): number | null {
  if (!anchor || !Number.isSafeInteger(anchor.serverTime) || anchor.serverTime! <= 0) return null;
  return anchor.serverTime! + Math.max(0, monotonicNow - anchor.receivedAt);
}

export function buildBitrateChart(samples: BitrateSample[], now: number | null, windowMs: number): BitrateChartData {
  if (now === null) return EMPTY_BITRATE_CHART;
  const start = now - windowMs;
  const points: BitratePoint[] = [];
  for (const sample of samples) {
    if (sample.timestamp < start || sample.timestamp > now) continue;
    const previous = points.at(-1);
    if (previous && sample.timestamp - previous.timestamp > 2000) {
      points.push({ timestamp: previous.timestamp + 1, bitrate: null });
    }
    points.push(sample);
  }
  return { points, domain: [start, now] };
}

export const formatBitrateTime = (timestamp: number): string => new Date(timestamp).toLocaleTimeString(undefined, {
  hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit",
});

export async function fetchBitrateHistory<T>(
  fetcher: (url: string, init?: RequestInit) => Promise<Response>,
  url: string,
  signal: AbortSignal,
): Promise<T & BitrateClockAnchor> {
  const response = await fetcher(url, { signal, cache: "no-store" });
  const payload = await response.json();
  if (!response.ok) throw new Error(payload?.error ?? "Unable to load stream bitrate history");
  if (!payload || typeof payload !== "object") throw new Error("Invalid stream bitrate history");
  return { ...payload, receivedAt: performance.now() };
}
